import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { Button, Dropdown, Icon, Input } from "antd"
import { useEffect, useState } from "react"
import { pipeline } from "@figurepos/fgr-lib-business-logic/lib/common/functional"
import { isNil, map, reject, uniqBy } from "ramda"
import { SelectItem } from "./components/SelectItem"
import { classNames } from "../../../utils/classNames"

export type SelectItemType<IdType = string> = {
    id: IdType
    label: React.ReactNode
    groupId?: string
    groupLabel?: string
    disabled?: boolean
}

export type SelectProps = {
    buttonClassName?: string
    dropDownClassName?: string
    disabled?: boolean
    data: SelectItemType[]
    showSearch?: boolean
    showSearchThreshold?: number
    labels?: Partial<SelectLabels>
    placeholder?: string
    value?: Maybe<string>
    size?: "small" | "normal" | "large"
    onChange?: (selectedId: string) => void
}

type SelectLabels = {
    noSelected: React.ReactNode
    singleSelected: (label: React.ReactNode) => React.ReactNode
}

const defaultLabels: SelectLabels = {
    noSelected: <span className="text-[#ff9800]">No item selected</span>,
    singleSelected: (label: string) => label,
}

export const Select = ({
    buttonClassName,
    dropDownClassName,
    disabled = false,
    data,
    showSearch = true,
    showSearchThreshold = 10,
    labels: labelsProp = defaultLabels,
    placeholder,
    value,
    size = "normal",
    onChange,
}: SelectProps) => {
    const [isOpen, setOpen] = useState(false)
    // Stored 'filteredData' array is changed when searching
    const [filteredData, setFilteredData] = useState(data)

    // Update the stored local state data
    useEffect(() => {
        setFilteredData(data)
    }, [data])

    const labels: SelectLabels = {
        ...defaultLabels,
        ...labelsProp,
    }

    const groupData = pipeline(
        filteredData,
        map(item => (item.groupId ? { id: item.groupId, label: item.groupLabel ?? "" } : null)),
        reject(isNil),
        uniqBy(group => (group as { id: string }).id)
    ) as { id: string; label: string }[]

    const onItemClicked = (itemId: string) => {
        onChange?.(itemId)
        setOpen(false)
    }

    const handleDropdownLabel = () => {
        if (value) {
            const selectedLabel = data.find(d => d.id === value)?.label
            return labels.singleSelected(selectedLabel || "")
        } else {
            const placeholderEl = placeholder ? <span className="opacity-30">{placeholder}</span> : null
            return placeholderEl ?? labels.noSelected
        }
    }

    const onSearch = e => {
        const searchValue: string = (e.target.value ?? "").trim().toLowerCase()

        const filteredItems = data.filter(item =>
            searchValue && typeof item.label === "string" ? item.label.trim().toLowerCase().includes(searchValue) : true
        )

        setFilteredData(filteredItems)
    }

    const renderEmptyMessage = () => {
        return <div className="py-3 text-center text-black/50">No items found</div>
    }

    const renderItems = (items: SelectItemType[]) => {
        return items.map(item => (
            <SelectItem
                key={item.id}
                isActive={value === item.id}
                item={item}
                onClick={onItemClicked}
                disabled={item.disabled}
            />
        ))
    }

    const renderContent = () => {
        if (groupData && groupData.length > 0) {
            const isEmpty = !groupData.some(group => filteredData.filter(item => item.groupId === group.id).length > 0)
            if (isEmpty) {
                return renderEmptyMessage()
            }
            return groupData.map(group => {
                const items = filteredData.filter(item => item.groupId === group.id)
                if (items.length < 1) {
                    return null
                }
                return (
                    <div key={group.id}>
                        <div className={classes.groupLabel}>{group.label}</div>
                        <div className="pl-2">{renderItems(items)}</div>
                    </div>
                )
            })
        } else if (filteredData.length > 0) {
            return renderItems(filteredData)
        } else {
            return renderEmptyMessage()
        }
    }

    const menu = (
        <div className={classNames(classes.dropdown, dropDownClassName)}>
            {data.length >= showSearchThreshold && showSearch && (
                <div className={classes.searchWrap}>
                    <Input.Search allowClear={true} placeholder="Search..." onChange={onSearch} />
                </div>
            )}
            <div className={classes.itemsWrap}>{renderContent()}</div>
        </div>
    )

    return (
        <Dropdown disabled={disabled} onVisibleChange={setOpen} visible={isOpen} overlay={menu} trigger={["click"]}>
            <Button className={classNames(classes.btn, classes.btnSize[size], buttonClassName)} role="combobox">
                <div className={classNames(classes.btnLabel)}>{handleDropdownLabel()}</div>
                <div className={classNames(classes.btnIconArrow)}>
                    {/* @ts-expect-error - antd v3 legacy issue. */}
                    <Icon type="down" />
                </div>
            </Button>
        </Dropdown>
    )
}

const classes = {
    dropdown:
        "relative flex max-h-[22rem] w-full max-w-md flex-col rounded border border-gray-100 bg-white py-2 shadow-md",
    itemsWrap: "overflow-x-hidden overflow-y-auto",
    searchWrap: "sticky top-0 z-10 bg-white pt-0.5 pb-2 px-2",
    groupLabel: "mt-1.5 pl-4 mb-1 text-[13px] text-black/50",
    btn: "!text-black/90 flex w-full items-center text-left",
    btnLabel: "flex-1 truncate",
    btnIconArrow: "mt-px text-[10px]",
    btnSize: {
        small: "h-[30px] text-13",
        normal: "h-[32px] text-14",
        large: "h-[38px] text-15",
    } as Record<NonNullable<SelectProps["size"]>, string>,
}
